import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from "recharts";
import {
  Users, Activity, AlertOctagon, Clock, TrendingUp, ShieldCheck,
  LogOut, RefreshCw, Sparkles, Building2, UserPlus, Plus, BarChart3, Trash2,
} from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TOKEN_KEY = "renalos_admin_token";

function getStoredToken() {
  try { return sessionStorage.getItem(ADMIN_TOKEN_KEY); } catch { return null; }
}
function storeToken(t) {
  try {
    if (t) sessionStorage.setItem(ADMIN_TOKEN_KEY, t);
    else sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch { /* ignore */ }
}

const RULE_LABEL = {
  eskd_transition_trigger: "ESKD transition trigger",
  fluid_overload_weight: "Fluid overload (weight)",
  kfre_high: "KFRE 2yr ≥ 20%",
  acr_spike: "ACR spike",
  egfr_rapid_decline: "eGFR rapid decline",
  missed_labs: "Missed labs",
  med_conflict: "Medication conflict",
  patient_shared_scorecard: "Patient shared scorecard",
};

const RULE_COLORS = ["#4ADE80", "#60A5FA", "#FBBF24", "#F87171", "#A78BFA", "#F472B6", "#22D3EE", "#94A3B8"];

/**
 * Internal Pilot Health dashboard.
 * Access is gated by an admin password (env: ADMIN_PASSWORD). Not part of
 * patient or provider auth — a completely separate token stored in sessionStorage.
 */
export default function Admin() {
  const [token, setToken] = useState(getStoredToken());
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [busy, setBusy] = useState(false);

  const [metrics, setMetrics] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loadErr, setLoadErr] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);

  async function doLogin(e) {
    e && e.preventDefault();
    setLoginErr(""); setBusy(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/login`, { password });
      storeToken(data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      setLoginErr(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  }

  async function loadAll(t = token) {
    if (!t) return;
    setLoadErr("");
    try {
      const [m, a] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/metrics`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${BACKEND_URL}/api/admin/activity`, { headers: { "X-Admin-Token": t } }),
      ]);
      setMetrics(m.data);
      setActivity(a.data);
      setLastRefresh(new Date());
    } catch (err) {
      if (err?.response?.status === 401) {
        storeToken(null); setToken(null);
      } else {
        setLoadErr(err?.response?.data?.detail || "Failed to load metrics");
      }
    }
  }

  useEffect(() => { if (token) loadAll(token); }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  function logout() {
    storeToken(null);
    setToken(null);
    setMetrics(null);
    setActivity([]);
  }

  const funnelPct = useMemo(() => {
    if (!metrics?.funnel?.onboarded) return { lab: 0, weight: 0 };
    const o = metrics.funnel.onboarded;
    return {
      lab: Math.round((metrics.funnel.with_baseline_lab / o) * 100),
      weight: Math.round((metrics.funnel.with_weight_and_lab / o) * 100),
    };
  }, [metrics]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#080A09] text-white flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="fixed top-4 right-4 z-50">
          <ThemeToggle variant="icon" />
        </div>
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-2xl bg-[#4ADE80] flex items-center justify-center ros-glow-primary">
              <ShieldCheck className="w-6 h-6 text-black" />
            </div>
            <div>
              <div className="text-lg font-semibold">Med Nexus · Pilot Health</div>
              <div className="text-xs text-[#A3ACA7]">Internal operations dashboard</div>
            </div>
          </div>
          <form onSubmit={doLogin} className="rounded-2xl bg-[#0F1312] border border-[#272E2B] p-6 space-y-4">
            <div>
              <label className="text-xs text-[#A3ACA7]">Admin password</label>
              <input
                type="password" autoFocus
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ADE80]"
                placeholder="Enter admin password"
              />
            </div>
            {loginErr && (
              <div className="text-sm text-[#F87171] bg-[#F87171]/10 border border-[#F87171]/20 rounded-lg p-3"
                   data-testid="admin-login-error">
                {loginErr}
              </div>
            )}
            <button
              type="submit" disabled={busy || !password}
              data-testid="admin-login-submit-button"
              className="w-full bg-[#4ADE80] hover:bg-[#22C55E] disabled:opacity-40 text-black font-semibold rounded-lg py-2.5 transition-colors">
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <p className="text-[11px] text-[#7A8480] pt-2 border-t border-[#272E2B]">
              Access is restricted to the Med Nexus internal ops team. Not for patient or clinician use.
            </p>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080A09] text-white" data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="border-b border-[#272E2B] bg-[#0A0D0C] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#4ADE80] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-black" />
            </div>
            <div>
              <div className="text-sm font-semibold">Med Nexus · Pilot Health</div>
              <div className="text-[11px] text-[#A3ACA7]">
                {metrics ? `Snapshot · ${metrics.month_key}` : "Loading…"}
                {lastRefresh && <> · Refreshed {lastRefresh.toLocaleTimeString()}</>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button onClick={() => loadAll()} data-testid="admin-refresh-button"
                    className="text-xs text-[#A3ACA7] hover:text-white flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#272E2B] hover:border-[#4ADE80]/40 transition-colors">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-button"
                    className="text-xs text-[#A3ACA7] hover:text-white flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#272E2B] hover:border-[#F87171]/40 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {loadErr && (
          <div className="text-sm text-[#F87171] bg-[#F87171]/10 border border-[#F87171]/20 rounded-lg p-3">
            {loadErr}
          </div>
        )}

        {!metrics ? (
          <div className="text-[#A3ACA7] text-sm">Loading metrics…</div>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-kpi-cards">
              <KpiCard
                icon={Users}
                label="Active patients (30d)"
                value={metrics.totals.active_patients}
                sub={`${metrics.totals.patients} total onboarded`}
                testid="kpi-active-patients"
                accent="#4ADE80"
              />
              <KpiCard
                icon={Clock}
                label={`RPM minutes · ${metrics.month_key}`}
                value={metrics.rpm.minutes_this_month.toLocaleString()}
                sub={`${metrics.rpm.billable_patients} billable · +${metrics.rpm.additional_20min_units} add-on unit${metrics.rpm.additional_20min_units === 1 ? "" : "s"}`}
                testid="kpi-rpm-minutes"
                accent="#60A5FA"
              />
              <KpiCard
                icon={AlertOctagon}
                label="Clinical alerts"
                value={metrics.alerts.total}
                sub={`${metrics.alerts.open} open · ${metrics.alerts.resolved} resolved`}
                testid="kpi-alerts"
                accent="#FBBF24"
              />
              <KpiCard
                icon={Sparkles}
                label="AI suggestions approved"
                value={metrics.ai.doctor_approved_suggestions}
                sub="Doctor-signed audit-log entries"
                testid="kpi-ai-approved"
                accent="#A78BFA"
              />
            </div>

            {/* Alerts breakdown + funnel */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="rounded-2xl bg-[#0F1312] border border-[#272E2B] p-5 lg:col-span-2" data-testid="admin-alerts-chart">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-sm font-semibold text-white">Alerts by clinical rule</div>
                    <div className="text-xs text-[#A3ACA7] mt-0.5">All-time · open = unresolved right now</div>
                  </div>
                  <div className="text-xs text-[#A3ACA7]">{metrics.alerts.this_month} raised this month</div>
                </div>
                {metrics.alerts.by_rule.length === 0 ? (
                  <div className="text-sm text-[#A3ACA7] py-10 text-center">No alerts yet.</div>
                ) : (
                  <div style={{ width: "100%", height: 260 }}>
                    <ResponsiveContainer>
                      <BarChart data={metrics.alerts.by_rule.map(r => ({ ...r, label: RULE_LABEL[r.rule] || r.rule }))}
                                layout="vertical" margin={{ top: 4, right: 12, left: 12, bottom: 4 }}>
                        <XAxis type="number" stroke="#4A544F" allowDecimals={false} tick={{ fill: "#A3ACA7", fontSize: 11 }} />
                        <YAxis type="category" dataKey="label" width={180} stroke="#4A544F" tick={{ fill: "#A3ACA7", fontSize: 11 }} />
                        <Tooltip
                          cursor={{ fill: "#ffffff08" }}
                          contentStyle={{ background: "#0A0D0C", border: "1px solid #272E2B", borderRadius: 8, fontSize: 12 }}
                          labelStyle={{ color: "#fff" }}
                          formatter={(value, name) => [value, name === "total" ? "Total" : "Open"]}
                        />
                        <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                          {metrics.alerts.by_rule.map((_, i) => (
                            <Cell key={i} fill={RULE_COLORS[i % RULE_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="rounded-2xl bg-[#0F1312] border border-[#272E2B] p-5" data-testid="admin-funnel">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-4 h-4 text-[#4ADE80]" />
                  <div className="text-sm font-semibold">Onboarding funnel</div>
                </div>
                <FunnelStep label="Onboarded"
                            value={metrics.funnel.onboarded} pct={100} color="#4ADE80" testid="funnel-onboarded" />
                <FunnelStep label="Baseline lab captured"
                            value={metrics.funnel.with_baseline_lab} pct={funnelPct.lab} color="#60A5FA" testid="funnel-lab" />
                <FunnelStep label="Weight + lab both logged"
                            value={metrics.funnel.with_weight_and_lab} pct={funnelPct.weight} color="#A78BFA" testid="funnel-weight" />

                <div className="mt-5 pt-4 border-t border-[#272E2B] text-xs text-[#A3ACA7] space-y-1">
                  <div className="flex justify-between"><span>Providers on platform</span><span className="text-white">{metrics.totals.providers}</span></div>
                  <div className="flex justify-between"><span>Providers active this month</span><span className="text-white">{metrics.rpm.active_providers_this_month}</span></div>
                </div>
              </div>
            </div>

            {/* Recent activity */}
            <div className="rounded-2xl bg-[#0F1312] border border-[#272E2B] p-5" data-testid="admin-activity-feed">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-4 h-4 text-[#60A5FA]" />
                <div className="text-sm font-semibold">Recent activity</div>
              </div>
              {activity.length === 0 ? (
                <div className="text-sm text-[#A3ACA7] py-6 text-center">No activity yet.</div>
              ) : (
                <ul className="divide-y divide-[#272E2B]">
                  {activity.slice(0, 20).map((row, i) => (
                    <li key={i} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`activity-row-${i}`}>
                      <span className={`w-2 h-2 rounded-full ${row.kind === "onboarded" ? "bg-[#4ADE80]" : "bg-[#F87171]"}`} />
                      <span className="text-white flex-1">{row.label}</span>
                      <span className="text-[11px] text-[#A3ACA7] whitespace-nowrap">
                        {row.timestamp ? new Date(row.timestamp).toLocaleString() : "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="text-[11px] text-[#7A8480] pt-2 pb-6">
              Med Nexus · internal pilot health snapshot · generated at {new Date(metrics.generated_at).toLocaleString()}.
              Numbers refresh on demand.
            </div>

            {/* Departments & Doctors administration */}
            <OrganizationPanel token={token} />

            {/* Phase 3d — Pilot telemetry (feature adoption across the 4 hubs) */}
            <TelemetryPanel token={token} />
          </>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="rounded-2xl bg-[#0F1312] border border-[#272E2B] p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}20` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
        <div className="text-xs text-[#A3ACA7]">{label}</div>
      </div>
      <div className="text-3xl font-semibold text-white tabular-nums" data-testid={`${testid}-value`}>{value}</div>
      <div className="text-[11px] text-[#A3ACA7] mt-1">{sub}</div>
    </div>
  );
}

function FunnelStep({ label, value, pct, color, testid }) {
  return (
    <div className="mb-3" data-testid={testid}>
      <div className="flex justify-between text-xs mb-1.5">
        <span className="text-[#A3ACA7]">{label}</span>
        <span className="text-white tabular-nums">{value} <span className="text-[#7A8480]">· {pct}%</span></span>
      </div>
      <div className="h-2 bg-[#1C201E] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(2, pct)}%`, background: color }} />
      </div>
    </div>
  );
}

/* ==================================================================
 * OrganizationPanel — Departments & Doctors CRUD (Phase 1)
 * Admin can add new hospital departments and provision doctor accounts.
 * ================================================================== */
function OrganizationPanel({ token }) {
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState("");
  const [dept, setDept] = useState({ key: "", name: "", description: "" });
  const [doc, setDoc] = useState({ name: "", email: "", department: "", password: "" });

  const headers = { "X-Admin-Token": token };
  async function load() {
    setErr("");
    try {
      const [d, k] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/departments`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/doctors`,     { headers }),
      ]);
      setDepartments(d.data.departments || []);
      setDoctors(k.data.doctors || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load organization data");
    }
  }
  useEffect(() => { if (token) load(); }, [token]);  // eslint-disable-line

  async function addDept(e) {
    e.preventDefault();
    setBusy(true); setErr(""); setFlash("");
    try {
      await axios.post(`${BACKEND_URL}/api/admin/departments`, dept, { headers });
      setFlash(`Added department "${dept.name}"`);
      setDept({ key: "", name: "", description: "" });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to add department");
    } finally { setBusy(false); }
  }

  async function addDoctor(e) {
    e.preventDefault();
    setBusy(true); setErr(""); setFlash("");
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/doctors`,
        { ...doc, department: doc.department || departments[0]?.key || "general" },
        { headers },
      );
      setFlash(`Added ${doc.name} (${doc.email}) · temp password: ${data.temp_password}`);
      setDoc({ name: "", email: "", department: "", password: "" });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to add doctor");
    } finally { setBusy(false); }
  }

  async function deleteDept(key) {
    if (!window.confirm(`Delete department "${key}"? Doctors must be reassigned first.`)) return;
    setBusy(true); setErr(""); setFlash("");
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/departments/${key}`, { headers });
      setFlash(`Department "${key}" deleted`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to delete department");
    } finally { setBusy(false); }
  }

  async function deleteDoctor(doctor) {
    if (!window.confirm(`Delete doctor "${doctor.name}" (${doctor.email})?`)) return;
    setBusy(true); setErr(""); setFlash("");
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/doctors/${doctor.id}`, { headers });
      setFlash(`Doctor "${doctor.name}" deleted`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to delete doctor");
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-4" data-testid="admin-org-panel">
      <div className="flex items-center gap-2 pt-4 border-t border-[#272E2B]">
        <Building2 className="w-4 h-4 text-[#60A5FA]" />
        <div className="text-sm font-semibold text-white">Organization · Departments & Doctors</div>
      </div>

      {err && <div className="text-sm text-[#F87171] bg-[#F87171]/10 border border-[#F87171]/20 rounded-lg p-3"
                    data-testid="admin-org-error">{err}</div>}
      {flash && <div className="text-sm text-[#4ADE80] bg-[#4ADE80]/10 border border-[#4ADE80]/20 rounded-lg p-3"
                     data-testid="admin-org-flash">{flash}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Add department */}
        <form onSubmit={addDept} className="rounded-2xl bg-[#0F1312] border border-[#272E2B] p-5 space-y-3"
              data-testid="add-department-form">
          <div className="flex items-center gap-2 mb-1">
            <Plus className="w-4 h-4 text-[#4ADE80]" />
            <div className="text-sm font-semibold">Add department</div>
          </div>
          <input value={dept.key} onChange={(e) => setDept({ ...dept, key: e.target.value })}
                 data-testid="dept-key-input"
                 placeholder="key (e.g. cardiology)"
                 className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#60A5FA]" required />
          <input value={dept.name} onChange={(e) => setDept({ ...dept, name: e.target.value })}
                 data-testid="dept-name-input"
                 placeholder="Display name (e.g. Cardiology)"
                 className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#60A5FA]" required />
          <input value={dept.description} onChange={(e) => setDept({ ...dept, description: e.target.value })}
                 data-testid="dept-description-input"
                 placeholder="Description (optional)"
                 className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#60A5FA]" />
          <button type="submit" disabled={busy} data-testid="dept-submit"
                  className="w-full bg-[#60A5FA] hover:bg-[#3B82F6] disabled:opacity-50 text-black font-semibold rounded-lg py-2 text-sm">
            {busy ? "Adding…" : "Add department"}
          </button>
          <div className="pt-3 border-t border-[#272E2B]">
            <div className="text-[11px] text-[#A3ACA7] mb-2">Existing ({departments.length})</div>
            <div className="flex flex-wrap gap-1.5" data-testid="dept-list">
              {departments.filter((d) => !d.inferred).map((d) => (
                <span key={d.id || d.key} data-testid={`dept-row-${d.key}`}
                      className="text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1.5"
                      style={{ color: d.accent, borderColor: `${d.accent}55`, background: `${d.accent}12` }}>
                  {d.name}
                  <button onClick={() => deleteDept(d.key)}
                          data-testid={`dept-delete-${d.key}`}
                          className="opacity-60 hover:opacity-100 hover:text-[#F87171]"
                          title="Delete department">
                    <Trash2 size={10} />
                  </button>
                </span>
              ))}
              {departments.filter((d) => d.inferred).map((d) => (
                <span key={d.key} className="text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1.5 opacity-60"
                      style={{ color: d.accent, borderColor: `${d.accent}33` }}
                      title="Inferred from provider users (not stored)">
                  {d.name} <span className="text-[9px]">·auto</span>
                </span>
              ))}
              {departments.length === 0 && <span className="text-[11px] text-[#7A8480]">No departments yet — add one.</span>}
            </div>
          </div>
        </form>

        {/* Add doctor */}
        <form onSubmit={addDoctor} className="rounded-2xl bg-[#0F1312] border border-[#272E2B] p-5 space-y-3"
              data-testid="add-doctor-form">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="w-4 h-4 text-[#A78BFA]" />
            <div className="text-sm font-semibold">Add doctor</div>
          </div>
          <input value={doc.name} onChange={(e) => setDoc({ ...doc, name: e.target.value })}
                 data-testid="doctor-name-input"
                 placeholder="Full name (e.g. Dr. Ada Lovelace)"
                 className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#A78BFA]" required />
          <input value={doc.email} onChange={(e) => setDoc({ ...doc, email: e.target.value })}
                 data-testid="doctor-email-input"
                 placeholder="email@hospital.org"
                 type="email"
                 className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#A78BFA]" required />
          <select value={doc.department} onChange={(e) => setDoc({ ...doc, department: e.target.value })}
                  data-testid="doctor-dept-select"
                  className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#A78BFA]" required>
            <option value="">Select department…</option>
            {departments.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
          </select>
          <input value={doc.password} onChange={(e) => setDoc({ ...doc, password: e.target.value })}
                 data-testid="doctor-password-input"
                 placeholder="Password (leave blank for auto-generated)"
                 className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#A78BFA]" />
          <button type="submit" disabled={busy || departments.length === 0} data-testid="doctor-submit"
                  className="w-full bg-[#A78BFA] hover:bg-[#8B5CF6] disabled:opacity-50 text-black font-semibold rounded-lg py-2 text-sm">
            {busy ? "Adding…" : "Provision doctor"}
          </button>
          <div className="pt-3 border-t border-[#272E2B]">
            <div className="text-[11px] text-[#A3ACA7] mb-2">Roster ({doctors.length})</div>
            <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="doctor-list">
              {doctors.map((u) => (
                <div key={u.id} data-testid={`doctor-row-${u.email}`}
                     className="flex items-center justify-between text-[11px] py-1 group">
                  <span className="text-white truncate">{u.name}</span>
                  <span className="text-[#A3ACA7] flex items-center gap-2">
                    {u.email} · <span className="uppercase tracking-wider">{u.department}</span>
                    <button onClick={() => deleteDoctor(u)}
                            data-testid={`doctor-delete-${u.email}`}
                            className="opacity-0 group-hover:opacity-70 hover:opacity-100 hover:text-[#F87171] transition-opacity"
                            title="Delete doctor">
                      <Trash2 size={11} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}


const HUB_LABEL = {
  "calc-hub":       "Calculators Hub",
  "care-planning":  "Care Planning",
  "digital-twin":   "Digital Twin",
  "diet-optimizer": "Diet Optimizer",
  "weight-fluid":   "Weight & Fluid",
};
const HUB_COLOR = {
  "calc-hub":       "#60A5FA",
  "care-planning":  "#4ADE80",
  "digital-twin":   "#A78BFA",
  "diet-optimizer": "#FBBF24",
  "weight-fluid":   "#F87171",
};

/* ==================================================================
 * TelemetryPanel — Phase 3d
 * Feature adoption dashboard across the 4 new hubs, aggregated per
 * (hub, specialty). Reads /api/admin/telemetry with the admin token.
 * ================================================================== */
function TelemetryPanel({ token }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!token) return;
    (async () => {
      setErr("");
      try {
        const { data: d } = await axios.get(
          `${BACKEND_URL}/api/admin/telemetry?days=${days}`,
          { headers: { "X-Admin-Token": token } },
        );
        setData(d);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load telemetry");
      }
    })();
  }, [token, days]);

  return (
    <div className="space-y-4" data-testid="admin-telemetry-panel">
      <div className="flex items-center gap-2 pt-4 border-t border-[#272E2B]">
        <BarChart3 className="w-4 h-4 text-[#A78BFA]" />
        <div className="text-sm font-semibold text-white">Feature adoption · anonymized telemetry</div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                data-testid="telemetry-window-select"
                className="ml-auto bg-[#1C201E] border border-[#272E2B] rounded-md text-[11px] px-2 py-1 outline-none focus:border-[#A78BFA]">
          {[7, 30, 90, 180].map((d) => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>

      {err && <div className="text-sm text-[#F87171]" data-testid="telemetry-error">{err}</div>}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="telemetry-totals">
            <div className="rounded-2xl bg-[#0F1312] border border-[#272E2B] p-4">
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Total events</div>
              <div className="text-3xl font-heading font-bold text-white mt-1 tabular-nums">{data.total_events}</div>
            </div>
            <div className="rounded-2xl bg-[#0F1312] border border-[#272E2B] p-4">
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Unique users</div>
              <div className="text-3xl font-heading font-bold text-white mt-1 tabular-nums">{data.total_unique_users}</div>
            </div>
            <div className="rounded-2xl bg-[#0F1312] border border-[#272E2B] p-4">
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Window</div>
              <div className="text-sm text-white mt-1">Since {data.since_day}</div>
              <div className="text-[11px] text-[#A3ACA7] mt-1">{data.hubs_covered.length} hubs tracked</div>
            </div>
          </div>

          <div className="rounded-2xl bg-[#0F1312] border border-[#272E2B] p-5" data-testid="telemetry-breakdown">
            <div className="text-[11px] uppercase tracking-wider text-[#A3ACA7] mb-3">
              Adoption by hub × specialty
            </div>
            {data.breakdown.length === 0 ? (
              <div className="text-sm text-[#A3ACA7]">No events yet in this window. Fire up a hub to seed data.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] text-[#A3ACA7] uppercase tracking-wider border-b border-[#272E2B]">
                    <th className="py-2 pr-4">Hub</th>
                    <th className="py-2 pr-4">Specialty</th>
                    <th className="py-2 pr-4 text-right">Events</th>
                    <th className="py-2 text-right">Unique users</th>
                  </tr>
                </thead>
                <tbody>
                  {data.breakdown.map((r, i) => {
                    const color = HUB_COLOR[r.hub] || "#A3ACA7";
                    return (
                      <tr key={i} className="border-t border-[#272E2B]"
                          data-testid={`telemetry-row-${r.hub}-${r.specialty || "any"}`}>
                        <td className="py-2.5 pr-4">
                          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                                style={{ color, background: `${color}18`, border: `1px solid ${color}40` }}>
                            {HUB_LABEL[r.hub] || r.hub}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 text-xs uppercase tracking-wider text-[#A3ACA7]">
                          {r.specialty || "—"}
                        </td>
                        <td className="py-2.5 pr-4 text-right font-mono text-white">{r.events}</td>
                        <td className="py-2.5 text-right font-mono text-white">{r.unique_users}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
